import { useCallback, useEffect, useRef, useState } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Image } from "@/libraries/design/components";
import { PlayPauseControls } from "@/libraries/design/components/play-pause-controls";
import { ColumnLayout, wacToken } from "@/libraries/design/foundations";
import { DataEventProps } from "@/libraries/wpl/foundations/src";

import { ValuePropCard, ValuePropCardData } from "./value-prop-card";

export type MediaAspectRatio = "1 / 1" | "16 / 9";

export interface ValuePropsProps {
    title: string;
    subtitle?: string;
    cards: ValuePropCardData[];
    mediaAspectRatio?: MediaAspectRatio;
    /** Duration in seconds each card auto-plays before transitioning */
    autoPlayDuration?: number;
    dataEventProps?: DataEventProps;
}

const AUTO_PLAY_DURATION_DEFAULT = 20;
const PROGRESS_INTERVAL_MS = 50;

const containerCss = css({
    paddingBlock: "80px",

    [media.above.lg]: {
        paddingBlock: "96px"
    },

    [media.below.sm]: {
        paddingBlock: token("space.800")
    }
});

const titleContainerCss = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.100"),
    marginBlockEnd: token("space.800"),
    [media.below.sm]: {
        marginBlockEnd: token("space.100")
    }
});

const zeroMarginCss = css({
    margin: 0
});

const desktopSectionCss = css({
    display: "block",

    [media.below.lg]: {
        display: "none"
    }
});

const nonDesktopSectionCss = css({
    "display": "none",

    [media.below.lg]: {
        display: "block"
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "div:nth-of-type(1)": {
        [media.below.sm]: {
            "gridTemplateColumns": "unset",
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            "> *": {
                gridColumn: "unset"
            }
        }
    }
});

const cardsWithPlayPauseCss = css({
    display: "flex",
    flexDirection: "row",
    gap: token("space.400"),
    alignItems: "flex-start",
    marginInlineStart: "-56px",

    [media.below.lg]: {
        marginInlineStart: "-48px",
        gap: token("space.100")
    },

    [media.below.sm]: {
        flexDirection: "column",
        marginInlineStart: 0,
        gap: token("space.150")
    }
});

const playPauseContainerCss = css({
    display: "flex",
    alignItems: "flex-start",
    flexShrink: 0
});

const cardListCss = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.400"),
    flex: 1,
    listStyle: "none",
    margin: 0,
    padding: 0
});

const mediaCss = css({
    flex: 1,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    minWidth: 0,

    [media.below.sm]: {
        width: "100%"
    }
});

const mediaContainerCss = css({
    position: "relative",
    width: "100%",
    overflow: "hidden",
    borderRadius: wacToken("corner.radius.card")
});

const MediaContent = ({ card }: { card: ValuePropCardData }) => {
    if (card.imageSrc) {
        return (
            <Image
                src={card.imageSrc}
                alt={card.mediaAlt || card.title}
                style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block"
                }}
            />
        );
    }

    if (card.videoSrc) {
        const videoFormat = card.videoSrc.split(".").at(-1);
        const videoType = videoFormat === "webm" ? "webm" : "mp4";

        return (
            <video
                data-value-props-video
                key={card.videoSrc}
                autoPlay
                muted
                loop
                playsInline
                aria-label={card.mediaAlt || card.title}
                style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block"
                }}
            >
                <source src={card.videoSrc} type={`video/${videoType}`} />
            </video>
        );
    }

    return null;
};

const ValueProps = ({
    title,
    subtitle,
    cards,
    mediaAspectRatio = "1 / 1",
    autoPlayDuration = AUTO_PLAY_DURATION_DEFAULT,
    dataEventProps
}: ValuePropsProps) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(true);
    const [progress, setProgress] = useState(0);
    const progressRef = useRef(0);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const activeCard = cards[activeIndex];
    // aspectRatio is used as a direct CSS value via inline style

    const goToNext = useCallback(() => {
        setActiveIndex((prev) => (prev + 1) % cards.length);
        setProgress(0);
        progressRef.current = 0;
    }, [cards.length]);

    const handleCardClick = useCallback(
        (index: number) => {
            if (index === activeIndex) {
                return;
            }
            setIsPlaying(false);
            setActiveIndex(index);
            setProgress(0);
            progressRef.current = 0;
        },
        [activeIndex]
    );

    const handlePlayPauseToggle = useCallback(() => {
        setIsPlaying((prev) => !prev);
    }, []);

    // Auto-play timer
    useEffect(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        if (!isPlaying) {
            return;
        }

        const totalSteps = (autoPlayDuration * 1000) / PROGRESS_INTERVAL_MS;
        const incrementPerStep = 100 / totalSteps;

        intervalRef.current = setInterval(() => {
            progressRef.current += incrementPerStep;

            if (progressRef.current >= 100) {
                goToNext();
            } else {
                setProgress(progressRef.current);
            }
        }, PROGRESS_INTERVAL_MS);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isPlaying, activeIndex, autoPlayDuration, goToNext]);

    // Reset progress when active index changes
    useEffect(() => {
        progressRef.current = 0;
        setProgress(0);
    }, [activeIndex]);

    // Sync video play/pause state
    useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        const video = container.querySelector(
            "video[data-value-props-video]"
        ) as HTMLVideoElement | null;
        if (!video) {
            return;
        }

        if (isPlaying) {
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            video.play().catch(() => {});
        } else {
            video.pause();
        }
    }, [isPlaying, activeIndex]);

    if (!cards || cards.length === 0) {
        return null;
    }

    const cardsContent = (
        <ul css={cardListCss}>
            {cards.map((card, index) => (
                <li key={index}>
                    <ValuePropCard
                        card={card}
                        isActive={index === activeIndex}
                        progress={
                            index === activeIndex
                                ? progress
                                : index < activeIndex
                                ? 100
                                : 0
                        }
                        onClick={() => handleCardClick(index)}
                        index={index}
                        dataEventProps={dataEventProps}
                    />
                </li>
            ))}
        </ul>
    );

    const mediaContent = activeCard && (
        <div css={mediaCss}>
            <div
                css={mediaContainerCss}
                style={{ aspectRatio: mediaAspectRatio }}
            >
                <MediaContent card={activeCard} />
            </div>
        </div>
    );

    const playPauseAndCards = (
        <div css={cardsWithPlayPauseCss}>
            <div css={playPauseContainerCss}>
                <PlayPauseControls
                    isPlaying={isPlaying}
                    onToggle={handlePlayPauseToggle}
                    mediaLabel={title}
                />
            </div>
            {cardsContent}
        </div>
    );

    return (
        <div ref={containerRef} css={containerCss} data-testid="value-props">
            <ColumnLayout layout="8-4">
                <div css={[zeroMarginCss, titleContainerCss]}>
                    <h2 css={zeroMarginCss}>{title}</h2>
                    {subtitle && <p css={zeroMarginCss}>{subtitle}</p>}
                </div>
            </ColumnLayout>
            <div css={desktopSectionCss}>
                <ColumnLayout layout="5-7">
                    {playPauseAndCards}
                    {mediaContent}
                </ColumnLayout>
            </div>
            <div css={nonDesktopSectionCss}>
                <ColumnLayout layout="6-6">
                    {playPauseAndCards}
                    {mediaContent}
                </ColumnLayout>
            </div>
        </div>
    );
};

export default ValueProps;
